import React from 'react'
import { Button, Input, Label, Modal, Textarea } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import PhoneInputIntl from '@/shared/ui/PhoneInputIntl'
import type { AffiliateLead } from '../types'
import { updateAffiliateLead } from '../services/affiliateLeadService'

export interface AffiliateLeadEditModalProps {
  open: boolean
  lead: AffiliateLead | null
  onClose: () => void
  onSaved?: () => void
}

export default function AffiliateLeadEditModal({ open, lead, onClose, onSaved }: AffiliateLeadEditModalProps) {
  const { add: toast } = useToast()
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [whatsapp, setWhatsapp] = React.useState('')
  const [instagram, setInstagram] = React.useState('')
  const [facebook, setFacebook] = React.useState('')
  const [youtube, setYoutube] = React.useState('')
  const [discord, setDiscord] = React.useState('')
  const [otherSocials, setOtherSocials] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setError(null)
    setSaving(false)
    setName(lead?.full_name ?? '')
    setEmail(lead?.email ?? '')
    setWhatsapp(lead?.whatsapp ?? '')
    setInstagram(lead?.social_instagram ?? '')
    setFacebook(lead?.social_facebook ?? '')
    setYoutube(lead?.social_youtube ?? '')
    setDiscord(lead?.social_discord ?? '')
    setOtherSocials(lead?.social_other ?? '')
    setNotes(lead?.notes ?? '')
  }, [open, lead])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!lead) return
    setError(null)

    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedWhatsapp = whatsapp.trim()

    if (!trimmedName || !trimmedEmail || !trimmedWhatsapp) {
      setError('Nome, e-mail e WhatsApp são obrigatórios.')
      return
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) {
      setError('Informe um e-mail válido.')
      return
    }

    setSaving(true)
    try {
      await updateAffiliateLead(lead.id, {
        full_name: trimmedName,
        email: trimmedEmail,
        whatsapp: trimmedWhatsapp,
        social_instagram: instagram || null,
        social_facebook: facebook || null,
        social_youtube: youtube || null,
        social_discord: discord || null,
        social_other: otherSocials || null,
        notes: notes || null,
      })
      onSaved?.()
      onClose()
    } catch (err: any) {
      const msg = err?.message ?? 'Não foi possível salvar as alterações. Tente novamente em instantes.'
      setError(msg)
      toast({
        variant: 'error',
        title: 'Falha ao salvar interessado',
        description: msg,
        soft: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">Editar interessado</h2>
          <p className="text-sm text-foreground/70">
            Ajuste os dados enviados pelo interessado no programa de afiliados.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit-lead-name">Nome completo *</Label>
            <Input
              id="edit-lead-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do interessado"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-lead-email">E-mail para contato *</Label>
            <Input
              id="edit-lead-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seuemail@exemplo.com"
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>WhatsApp *</Label>
          <PhoneInputIntl
            value={whatsapp}
            onChange={setWhatsapp}
            defaultCountry="BR"
            placeholderBR="(11) 98888-7777"
            required
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit-lead-instagram">Instagram</Label>
            <Input
              id="edit-lead-instagram"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@seuusuario ou link do perfil"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-lead-facebook">Facebook</Label>
            <Input
              id="edit-lead-facebook"
              value={facebook}
              onChange={(e) => setFacebook(e.target.value)}
              placeholder="Link da página ou perfil"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit-lead-youtube">YouTube</Label>
            <Input
              id="edit-lead-youtube"
              value={youtube}
              onChange={(e) => setYoutube(e.target.value)}
              placeholder="Link do canal"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-lead-discord">Discord</Label>
            <Input
              id="edit-lead-discord"
              value={discord}
              onChange={(e) => setDiscord(e.target.value)}
              placeholder="Servidor ou usuário (se relevante)"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-lead-other">Outras mídias / links relevantes</Label>
          <Input
            id="edit-lead-other"
            value={otherSocials}
            onChange={(e) => setOtherSocials(e.target.value)}
            placeholder="Telegram, blog, comunidade, etc."
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-lead-notes">Observações</Label>
          <Textarea
            id="edit-lead-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notas internas sobre o perfil do interessado."
            rows={3}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving || !lead}>
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
